import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import {
  PublicClientApplication,
  EventType,
  type AuthenticationResult,
} from '@azure/msal-browser';
import { msalConfig } from '@/auth/authConfig';
import { AuthProvider } from '@/auth/AuthProvider';
import { App } from '@/App';
import { DemoApp } from '@/DemoApp';
import './styles.css';

const isDemo =
  !import.meta.env.VITE_AZURE_CLIENT_ID ||
  import.meta.env.VITE_AZURE_CLIENT_ID === 'your-app-registration-client-id';

function renderError(title: string, body: string, detail?: string) {
  const root = document.getElementById('root')!;
  root.innerHTML = `
    <div style="max-width:480px;margin:80px auto;padding:32px;font-family:-apple-system,Segoe UI,sans-serif;">
      <h1 style="margin:0 0 12px;color:#0078d4;font-size:22px;">Prism sign-in issue</h1>
      <h2 style="margin:0 0 8px;font-size:16px;font-weight:600;">${title}</h2>
      <p style="margin:0 0 16px;color:#555;line-height:1.5;">${body}</p>
      ${detail ? `<pre style="background:#f3f3f3;padding:12px;border-radius:6px;font-size:11px;overflow:auto;max-height:200px;">${detail.replace(/</g, '&lt;')}</pre>` : ''}
      <div style="margin-top:24px;display:flex;gap:8px;flex-wrap:wrap;">
        <button id="prism-retry" style="padding:10px 18px;background:#0078d4;color:white;border:0;border-radius:6px;font-size:14px;cursor:pointer;">Try again</button>
        <button id="prism-reset" style="padding:10px 18px;background:#d13438;color:white;border:0;border-radius:6px;font-size:14px;cursor:pointer;">Clear session &amp; reset</button>
      </div>
    </div>
  `;
  document
    .getElementById('prism-retry')
    ?.addEventListener('click', () => location.reload());
  document.getElementById('prism-reset')?.addEventListener('click', () => {
    try {
      localStorage.clear();
      sessionStorage.clear();
      // Clear all msal cookies
      document.cookie.split(';').forEach((c) => {
        const name = c.split('=')[0].trim();
        if (name.startsWith('msal.'))
          document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
      });
    } catch {
      /* ignore */
    }
    location.href = '/';
  });
}

if (isDemo) {
  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      <DemoApp />
    </StrictMode>,
  );
} else {
  const msalInstance = new PublicClientApplication(msalConfig);

  // ── Log all MSAL events to console for easy debugging ───────────────────
  msalInstance.addEventCallback((event) => {
    if (event.eventType === EventType.LOGIN_FAILURE) {
      console.error('[Prism MSAL] LOGIN_FAILURE:', event.error);
    } else if (event.eventType === EventType.LOGIN_SUCCESS) {
      const payload = event.payload as AuthenticationResult | null;
      if (payload?.account) {
        msalInstance.setActiveAccount(payload.account);
        console.info('[Prism MSAL] LOGIN_SUCCESS:', payload.account.username);
      }
    } else if (event.eventType === EventType.ACQUIRE_TOKEN_FAILURE) {
      console.warn('[Prism MSAL] ACQUIRE_TOKEN_FAILURE:', event.error);
    } else if (event.eventType === EventType.HANDLE_REDIRECT_END) {
      console.info('[Prism MSAL] HANDLE_REDIRECT_END');
    }
  });

  // ── Bootstrap sequence ──────────────────────────────────────────────────
  msalInstance
    .initialize()
    .then(async () => {
      // Critical: must run on every page load, exactly once.
      // Returns null if there's no pending redirect (normal app open).
      // Returns AuthenticationResult if we just came back from Microsoft login.
      let redirectResponse: AuthenticationResult | null = null;
      try {
        redirectResponse = await msalInstance.handleRedirectPromise();
      } catch (err) {
        console.error('[Prism MSAL] handleRedirectPromise failed:', err);
        renderError(
          'Sign-in redirect could not be processed',
          "This usually means the redirect URI in Azure AD doesn't match this page's URL, or the app registration is missing the Single-page application platform. Reset and try again.",
          err instanceof Error ? err.message : String(err),
        );
        return;
      }

      if (redirectResponse?.account) {
        msalInstance.setActiveAccount(redirectResponse.account);
      }

      // Always set an active account if we have one cached
      if (
        !msalInstance.getActiveAccount() &&
        msalInstance.getAllAccounts().length > 0
      ) {
        msalInstance.setActiveAccount(msalInstance.getAllAccounts()[0]);
      }

      // Render
      createRoot(document.getElementById('root')!).render(
        <StrictMode>
          <AuthProvider msalInstance={msalInstance}>
            <App />
          </AuthProvider>
        </StrictMode>,
      );
    })
    .catch((err) => {
      console.error('[Prism MSAL] initialize failed:', err);
      renderError(
        'MSAL could not initialize',
        'The sign-in service failed to start. Check that VITE_AZURE_CLIENT_ID and VITE_AZURE_TENANT_ID in .env match your app registration.',
        err instanceof Error ? err.message : String(err),
      );
    });
}
